import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import chatLimitsService from "../../api/services/chatLimitsService";
import adminService from "../../api/services/adminService";
import { confirmDanger } from "../../utils/confirmDialog";
import SectionCard from "../../components/common/SectionCard";
import PaginationBar from "../../components/common/PaginationBar";

const ALLOWED = ["Admin", "Developer"];
const OVERRIDES_PAGE_SIZE = 20;
const THEME = { primary: "#FB565F" };
const inputCls =
  "border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white text-gray-800 focus:ring-2 focus:ring-red-300 focus:border-red-300 outline-none w-full";

function limitToInput(value) {
  if (value == null || value === "") return "";
  return String(value);
}

function parseLimit(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

function displayName(user) {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();
  return name || user?.email || user?.contactNo || `User #${user?.userId || user?.id}`;
}

function formatOverrideLimit(value) {
  if (value == null) return "Inherit";
  return String(value);
}

function formatSavedLimit(value) {
  if (value == null || value === "") return "Unlimited";
  return String(value);
}

function CurrentValue({ value }) {
  return (
    <p className="text-xs text-gray-500 mt-1">
      Previous: <span className="font-semibold text-gray-800">{value}</span>
    </p>
  );
}

function LimitField({ label, value, onChange, previous }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">{label}</span>
      <input
        className={`${inputCls} mt-1`}
        type="number"
        min="0"
        placeholder="Unlimited"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <CurrentValue value={formatSavedLimit(previous)} />
    </label>
  );
}

const ChatLimitsPage = () => {
  const userRole = localStorage.getItem("adminUserRole") || "";
  const [loading, setLoading] = useState(true);
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [expiringPremium, setExpiringPremium] = useState(false);

  const [dailyLimit, setDailyLimit] = useState("");
  const [weeklyLimit, setWeeklyLimit] = useState("");
  const [perChatMobile, setPerChatMobile] = useState("");
  const [perChatWeb, setPerChatWeb] = useState("");
  const [premiumDaily, setPremiumDaily] = useState("");
  const [premiumWeekly, setPremiumWeekly] = useState("");
  const [premiumPerChatMobile, setPremiumPerChatMobile] = useState("");
  const [premiumPerChatWeb, setPremiumPerChatWeb] = useState("");
  const [restrictAll, setRestrictAll] = useState(false);
  const [savedGlobal, setSavedGlobal] = useState({
    dailyLimit: null,
    weeklyLimit: null,
    perChatLimitMobile: null,
    perChatLimitWeb: null,
    premiumDailyLimit: null,
    premiumWeeklyLimit: null,
    premiumPerChatLimitMobile: null,
    premiumPerChatLimitWeb: null,
    restricted: false,
    previousDailyLimit: null,
    previousWeeklyLimit: null,
    previousPerChatLimitMobile: null,
    previousPerChatLimitWeb: null,
    previousPremiumDailyLimit: null,
    previousPremiumWeeklyLimit: null,
    previousPremiumPerChatLimitMobile: null,
    previousPremiumPerChatLimitWeb: null,
    previousRestricted: null,
  });
  const [appliedDefaults, setAppliedDefaults] = useState({
    mobilePerChatLimit: 10,
    webPerChatLimit: 7,
  });
  const [overrides, setOverrides] = useState([]);
  const [overridesPage, setOverridesPage] = useState(1);
  const [overridesTotal, setOverridesTotal] = useState(0);
  const [overridesTotalPages, setOverridesTotalPages] = useState(1);
  const [overridesLoading, setOverridesLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDaily, setUserDaily] = useState("");
  const [userWeekly, setUserWeekly] = useState("");
  const [userRestricted, setUserRestricted] = useState(false);
  const [userQuota, setUserQuota] = useState(null);
  const [savedUser, setSavedUser] = useState(null);
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const loadOverrides = useCallback(async (page = overridesPage) => {
    setOverridesLoading(true);
    try {
      const data = await chatLimitsService.getOverrides({
        page,
        limit: OVERRIDES_PAGE_SIZE,
      });
      setOverrides(Array.isArray(data?.users) ? data.users : []);
      setOverridesPage(data?.page || page);
      setOverridesTotal(data?.total ?? 0);
      setOverridesTotalPages(data?.totalPages ?? 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load user overrides");
    } finally {
      setOverridesLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await chatLimitsService.get();
      const global = data?.global || {};
      const defaults = data?.appliedDefaults || {};
      setDailyLimit(limitToInput(global.dailyLimit));
      setWeeklyLimit(limitToInput(global.weeklyLimit));
      setPerChatMobile(limitToInput(global.perChatLimitMobile));
      setPerChatWeb(limitToInput(global.perChatLimitWeb));
      setPremiumDaily(limitToInput(global.premiumDailyLimit));
      setPremiumWeekly(limitToInput(global.premiumWeeklyLimit));
      setPremiumPerChatMobile(limitToInput(global.premiumPerChatLimitMobile));
      setPremiumPerChatWeb(limitToInput(global.premiumPerChatLimitWeb));
      setRestrictAll(Boolean(global.restricted));
      setAppliedDefaults({
        mobilePerChatLimit: defaults.mobilePerChatLimit ?? 10,
        webPerChatLimit: defaults.webPerChatLimit ?? 7,
      });
      setSavedGlobal({
        dailyLimit: global.dailyLimit ?? null,
        weeklyLimit: global.weeklyLimit ?? null,
        perChatLimitMobile: global.perChatLimitMobile ?? null,
        perChatLimitWeb: global.perChatLimitWeb ?? null,
        premiumDailyLimit: global.premiumDailyLimit ?? null,
        premiumWeeklyLimit: global.premiumWeeklyLimit ?? null,
        premiumPerChatLimitMobile: global.premiumPerChatLimitMobile ?? null,
        premiumPerChatLimitWeb: global.premiumPerChatLimitWeb ?? null,
        restricted: Boolean(global.restricted),
        previousDailyLimit: global.previousDailyLimit ?? null,
        previousWeeklyLimit: global.previousWeeklyLimit ?? null,
        previousPerChatLimitMobile: global.previousPerChatLimitMobile ?? null,
        previousPerChatLimitWeb: global.previousPerChatLimitWeb ?? null,
        previousPremiumDailyLimit: global.previousPremiumDailyLimit ?? null,
        previousPremiumWeeklyLimit: global.previousPremiumWeeklyLimit ?? null,
        previousPremiumPerChatLimitMobile: global.previousPremiumPerChatLimitMobile ?? null,
        previousPremiumPerChatLimitWeb: global.previousPremiumPerChatLimitWeb ?? null,
        previousRestricted: global.previousRestricted ?? null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Failed to load chat limits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadOverrides(overridesPage);
  }, [overridesPage, loadOverrides]);

  useEffect(() => {
    const onClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (!searchQuery.trim() || selectedUser) {
      setSearchResults([]);
      setShowDropdown(false);
      return undefined;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await adminService.searchUsersForSubscription(searchQuery.trim());
        setSearchResults(res?.users || []);
        setShowDropdown(true);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, selectedUser]);

  const saveGlobal = async () => {
    setSavingGlobal(true);
    try {
      await toast.promise(
        chatLimitsService.updateGlobal({
          dailyLimit: parseLimit(dailyLimit),
          weeklyLimit: parseLimit(weeklyLimit),
          perChatLimitMobile: parseLimit(perChatMobile),
          perChatLimitWeb: parseLimit(perChatWeb),
          premiumDailyLimit: parseLimit(premiumDaily),
          premiumWeeklyLimit: parseLimit(premiumWeekly),
          premiumPerChatLimitMobile: parseLimit(premiumPerChatMobile),
          premiumPerChatLimitWeb: parseLimit(premiumPerChatWeb),
          restricted: restrictAll,
        }),
        {
          loading: "Saving global limits...",
          success: "Global chat limits saved",
          error: (e) => e?.response?.data?.message || e?.message || "Failed to save",
        }
      );
      await load();
      await loadOverrides(overridesPage);
    } finally {
      setSavingGlobal(false);
    }
  };

  const selectUser = async (user) => {
    setSelectedUser(user);
    setSearchQuery(displayName(user));
    setShowDropdown(false);
    try {
      const data = await chatLimitsService.getUser(user.id);
      const override = data?.override;
      const quota = data?.quota || null;
      setUserDaily(limitToInput(override?.dailyLimit ?? quota?.dailyLimit));
      setUserWeekly(limitToInput(override?.weeklyLimit ?? quota?.weeklyLimit));
      setUserRestricted(Boolean(override ? override.restricted : quota?.restricted));
      setUserQuota(quota);
      setSavedUser({
        hasOverride: Boolean(override),
        isPremium: Boolean(quota?.isPremium),
        dailyLimit: override?.dailyLimit ?? quota?.dailyLimit ?? null,
        weeklyLimit: override?.weeklyLimit ?? quota?.weeklyLimit ?? null,
        restricted: Boolean(override ? override.restricted : quota?.restricted),
        previousDailyLimit: override?.previousDailyLimit ?? null,
        previousWeeklyLimit: override?.previousWeeklyLimit ?? null,
        previousRestricted: override?.previousRestricted ?? null,
        globalDaily: data?.global?.dailyLimit ?? null,
        globalWeekly: data?.global?.weeklyLimit ?? null,
        globalPerChatMobile: data?.global?.perChatLimitMobile ?? null,
        globalPerChatWeb: data?.global?.perChatLimitWeb ?? null,
        globalPremiumDaily: data?.global?.premiumDailyLimit ?? null,
        globalPremiumWeekly: data?.global?.premiumWeeklyLimit ?? null,
        globalPremiumPerChatMobile: data?.global?.premiumPerChatLimitMobile ?? null,
        globalPremiumPerChatWeb: data?.global?.premiumPerChatLimitWeb ?? null,
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not load this user");
    }
  };

  const saveUser = async () => {
    if (!selectedUser?.id) {
      toast.error("Select a user first");
      return;
    }
    setSavingUser(true);
    try {
      await toast.promise(
        chatLimitsService.upsertUser(selectedUser.id, {
          dailyLimit: parseLimit(userDaily),
          weeklyLimit: parseLimit(userWeekly),
          restricted: userRestricted,
        }),
        {
          loading: "Saving user limits...",
          success: "User chat limits saved",
          error: (e) => e?.response?.data?.message || e?.message || "Failed to save",
        }
      );
      const data = await chatLimitsService.getUser(selectedUser.id);
      const override = data?.override;
      const quota = data?.quota || null;
      setUserQuota(quota);
      setSavedUser({
        hasOverride: Boolean(override),
        isPremium: Boolean(quota?.isPremium),
        dailyLimit: override?.dailyLimit ?? quota?.dailyLimit ?? null,
        weeklyLimit: override?.weeklyLimit ?? quota?.weeklyLimit ?? null,
        restricted: Boolean(override ? override.restricted : quota?.restricted),
        previousDailyLimit: override?.previousDailyLimit ?? null,
        previousWeeklyLimit: override?.previousWeeklyLimit ?? null,
        previousRestricted: override?.previousRestricted ?? null,
        globalDaily: data?.global?.dailyLimit ?? null,
        globalWeekly: data?.global?.weeklyLimit ?? null,
        globalPerChatMobile: data?.global?.perChatLimitMobile ?? null,
        globalPerChatWeb: data?.global?.perChatLimitWeb ?? null,
        globalPremiumDaily: data?.global?.premiumDailyLimit ?? null,
        globalPremiumWeekly: data?.global?.premiumWeeklyLimit ?? null,
        globalPremiumPerChatMobile: data?.global?.premiumPerChatLimitMobile ?? null,
        globalPremiumPerChatWeb: data?.global?.premiumPerChatLimitWeb ?? null,
      });
      await load();
      await loadOverrides(overridesPage);
    } finally {
      setSavingUser(false);
    }
  };

  const expirePremiumForSelected = async () => {
    if (!selectedUser?.id) {
      toast.error("Select a user first");
      return;
    }
    const ok = await confirmDanger(
      `Expire premium for ${displayName(selectedUser)} (ID ${selectedUser.id})? They will lose premium on web and mobile immediately.`,
      "Expire user premium?"
    );
    if (!ok) return;
    setExpiringPremium(true);
    try {
      const res = await toast.promise(adminService.expireUserPremium(selectedUser.id), {
        loading: "Expiring premium...",
        success: (r) => r?.message || "Premium expired",
        error: (e) => e?.message || e?.response?.data?.message || "Failed to expire premium",
      });
      const data = await chatLimitsService.getUser(selectedUser.id);
      const override = data?.override;
      const quota = data?.quota || null;
      setUserQuota(quota);
      setSavedUser({
        hasOverride: Boolean(override),
        isPremium: Boolean(quota?.isPremium),
        dailyLimit: override?.dailyLimit ?? quota?.dailyLimit ?? null,
        weeklyLimit: override?.weeklyLimit ?? quota?.weeklyLimit ?? null,
        restricted: Boolean(override ? override.restricted : quota?.restricted),
        previousDailyLimit: override?.previousDailyLimit ?? null,
        previousWeeklyLimit: override?.previousWeeklyLimit ?? null,
        previousRestricted: override?.previousRestricted ?? null,
        globalDaily: data?.global?.dailyLimit ?? null,
        globalWeekly: data?.global?.weeklyLimit ?? null,
        globalPerChatMobile: data?.global?.perChatLimitMobile ?? null,
        globalPerChatWeb: data?.global?.perChatLimitWeb ?? null,
        globalPremiumDaily: data?.global?.premiumDailyLimit ?? null,
        globalPremiumWeekly: data?.global?.premiumWeeklyLimit ?? null,
        globalPremiumPerChatMobile: data?.global?.premiumPerChatLimitMobile ?? null,
        globalPremiumPerChatWeb: data?.global?.premiumPerChatLimitWeb ?? null,
      });
      if (res?.isPremium === false) {
        setSelectedUser((prev) => (prev ? { ...prev, isPremium: 0 } : prev));
      }
    } finally {
      setExpiringPremium(false);
    }
  };

  const clearUser = async (userId) => {
    try {
      await toast.promise(chatLimitsService.deleteUser(userId), {
        loading: "Removing override...",
        success: "User now follows global limits",
        error: (e) => e?.response?.data?.message || e?.message || "Failed to remove",
      });
      if (Number(selectedUser?.id) === Number(userId)) {
        setSelectedUser(null);
        setSearchQuery("");
        setUserQuota(null);
        setSavedUser(null);
        setUserDaily("");
        setUserWeekly("");
        setUserRestricted(false);
      }
      await load();
      await loadOverrides(overridesPage);
    } catch {
      // toast already shown
    }
  };

  if (!ALLOWED.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="p-4 sm:p-6 w-full min-w-0 overflow-x-hidden bg-gray-50/60 min-h-[calc(100vh-4rem)] space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Chat Limits</h1>
        <p className="text-sm text-gray-500 mt-1">
          Set separate chat limits for non-premium and premium users. Non-premium currently uses
          mobile 10 / web 7 per chat; premium is unlimited until you set a cap.
        </p>
      </div>

      <SectionCard
        title="All users"
        subtitle="Leave a field empty for unlimited. Restrict all users blocks sending except users with a personal override that is not restricted."
      >
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            <div className="mb-4 rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-gray-800 space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#FB565F]">
                Current global limits
              </p>
              <p>
                <span className="font-semibold">Non-premium:</span> mobile per chat{" "}
                {formatSavedLimit(savedGlobal.perChatLimitMobile)} · web per chat{" "}
                {formatSavedLimit(savedGlobal.perChatLimitWeb)} · daily{" "}
                {formatSavedLimit(savedGlobal.dailyLimit)} · weekly{" "}
                {formatSavedLimit(savedGlobal.weeklyLimit)}
              </p>
              <p>
                <span className="font-semibold">Premium:</span> mobile per chat{" "}
                {formatSavedLimit(savedGlobal.premiumPerChatLimitMobile)} · web per chat{" "}
                {formatSavedLimit(savedGlobal.premiumPerChatLimitWeb)} · daily{" "}
                {formatSavedLimit(savedGlobal.premiumDailyLimit)} · weekly{" "}
                {formatSavedLimit(savedGlobal.premiumWeeklyLimit)}
              </p>
              <p>
                Restrict all:{" "}
                <span className="font-semibold">{savedGlobal.restricted ? "Yes" : "No"}</span>
              </p>
              <p className="text-xs text-gray-500">
                Previous non-premium — mobile{" "}
                {formatSavedLimit(
                  savedGlobal.previousPerChatLimitMobile ?? appliedDefaults.mobilePerChatLimit
                )}
                , web{" "}
                {formatSavedLimit(
                  savedGlobal.previousPerChatLimitWeb ?? appliedDefaults.webPerChatLimit
                )}
                , daily {formatSavedLimit(savedGlobal.previousDailyLimit)}, weekly{" "}
                {formatSavedLimit(savedGlobal.previousWeeklyLimit)}
              </p>
              <p className="text-xs text-gray-500">
                Previous premium — mobile{" "}
                {formatSavedLimit(savedGlobal.previousPremiumPerChatLimitMobile)}, web{" "}
                {formatSavedLimit(savedGlobal.previousPremiumPerChatLimitWeb)}, daily{" "}
                {formatSavedLimit(savedGlobal.previousPremiumDailyLimit)}, weekly{" "}
                {formatSavedLimit(savedGlobal.previousPremiumWeeklyLimit)}
                {savedGlobal.previousRestricted == null
                  ? ""
                  : ` · restrict ${savedGlobal.previousRestricted ? "Yes" : "No"}`}
              </p>
            </div>

            <p className="text-sm font-semibold text-gray-900 mb-2">Non-premium users</p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <LimitField
                label="Mobile per-chat limit"
                value={perChatMobile}
                onChange={setPerChatMobile}
                previous={
                  savedGlobal.previousPerChatLimitMobile ??
                  savedGlobal.perChatLimitMobile ??
                  appliedDefaults.mobilePerChatLimit
                }
              />
              <LimitField
                label="Web per-chat limit"
                value={perChatWeb}
                onChange={setPerChatWeb}
                previous={
                  savedGlobal.previousPerChatLimitWeb ??
                  savedGlobal.perChatLimitWeb ??
                  appliedDefaults.webPerChatLimit
                }
              />
              <LimitField
                label="Daily message limit"
                value={dailyLimit}
                onChange={setDailyLimit}
                previous={savedGlobal.previousDailyLimit ?? savedGlobal.dailyLimit}
              />
              <LimitField
                label="Weekly message limit"
                value={weeklyLimit}
                onChange={setWeeklyLimit}
                previous={savedGlobal.previousWeeklyLimit ?? savedGlobal.weeklyLimit}
              />
            </div>

            <p className="text-sm font-semibold text-gray-900 mt-6 mb-2">Premium users</p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <LimitField
                label="Mobile per-chat limit"
                value={premiumPerChatMobile}
                onChange={setPremiumPerChatMobile}
                previous={
                  savedGlobal.previousPremiumPerChatLimitMobile ??
                  savedGlobal.premiumPerChatLimitMobile
                }
              />
              <LimitField
                label="Web per-chat limit"
                value={premiumPerChatWeb}
                onChange={setPremiumPerChatWeb}
                previous={
                  savedGlobal.previousPremiumPerChatLimitWeb ??
                  savedGlobal.premiumPerChatLimitWeb
                }
              />
              <LimitField
                label="Daily message limit"
                value={premiumDaily}
                onChange={setPremiumDaily}
                previous={savedGlobal.previousPremiumDailyLimit ?? savedGlobal.premiumDailyLimit}
              />
              <LimitField
                label="Weekly message limit"
                value={premiumWeekly}
                onChange={setPremiumWeekly}
                previous={savedGlobal.previousPremiumWeeklyLimit ?? savedGlobal.premiumWeeklyLimit}
              />
            </div>

            <label className="flex flex-col justify-end gap-1 mt-6">
              <span className="flex items-center gap-3">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={restrictAll}
                  onChange={(e) => setRestrictAll(e.target.checked)}
                />
                <span className="text-sm font-medium text-gray-800">Restrict all users from chatting</span>
              </span>
              <CurrentValue
                value={
                  savedGlobal.previousRestricted == null
                    ? savedGlobal.restricted
                      ? "Yes"
                      : "No"
                    : savedGlobal.previousRestricted
                      ? "Yes"
                      : "No"
                }
              />
            </label>
          </>
        )}
        <div className="mt-4">
          <button
            type="button"
            disabled={savingGlobal || loading}
            onClick={saveGlobal}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: THEME.primary }}
          >
            {savingGlobal ? "Saving…" : "Save global settings"}
          </button>
        </div>
      </SectionCard>

      <SectionCard
        title="Restrict or limit a specific user"
        subtitle="Search by name, email, phone, or user ID. Empty daily/weekly fields inherit the global limit."
      >
        <div className="relative max-w-xl" ref={dropdownRef}>
          <input
            className={inputCls}
            placeholder="Search user by name, email, phone, or ID"
            value={searchQuery}
            onChange={(e) => {
              setSelectedUser(null);
              setUserQuota(null);
              setSavedUser(null);
              setSearchQuery(e.target.value);
            }}
            onFocus={() => {
              if (searchResults.length) setShowDropdown(true);
            }}
          />
          {searching ? (
            <p className="text-xs text-gray-400 mt-1">Searching…</p>
          ) : null}
          {showDropdown && searchResults.length > 0 ? (
            <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
              {searchResults.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                  onClick={() => selectUser(u)}
                >
                  <span className="font-medium text-gray-900">{displayName(u)}</span>
                  <span className="block text-xs text-gray-500">
                    ID {u.id}
                    {u.email ? ` · ${u.email}` : ""}
                    {u.contactNo ? ` · ${u.contactNo}` : ""}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {selectedUser ? (
          <div className="mt-4 space-y-4">
            <p className="text-sm text-gray-700">
              Selected: <span className="font-semibold">{displayName(selectedUser)}</span>{" "}
              <span className="text-gray-400">(ID {selectedUser.id})</span>
              {savedUser ? (
                <span className="ml-2 text-xs font-semibold text-[#FB565F]">
                  {savedUser.isPremium ? "Premium" : "Non-premium"}
                </span>
              ) : null}
            </p>
            {savedUser ? (
              <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-gray-800">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-[#FB565F] mb-1">
                  Previous limit for this user
                </p>
                <p>
                  Daily:{" "}
                  <span className="font-semibold">
                    {savedUser.hasOverride
                      ? formatOverrideLimit(savedUser.dailyLimit)
                      : `${formatSavedLimit(savedUser.dailyLimit)} (global)`}
                  </span>
                  {" · "}
                  Weekly:{" "}
                  <span className="font-semibold">
                    {savedUser.hasOverride
                      ? formatOverrideLimit(savedUser.weeklyLimit)
                      : `${formatSavedLimit(savedUser.weeklyLimit)} (global)`}
                  </span>
                  {" · "}
                  Restricted:{" "}
                  <span className="font-semibold">{savedUser.restricted ? "Yes" : "No"}</span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {savedUser.isPremium ? "Premium" : "Non-premium"} global defaults — daily{" "}
                  {formatSavedLimit(
                    savedUser.isPremium ? savedUser.globalPremiumDaily : savedUser.globalDaily
                  )}
                  , weekly{" "}
                  {formatSavedLimit(
                    savedUser.isPremium ? savedUser.globalPremiumWeekly : savedUser.globalWeekly
                  )}
                  , mobile per chat{" "}
                  {formatSavedLimit(
                    savedUser.isPremium
                      ? savedUser.globalPremiumPerChatMobile
                      : savedUser.globalPerChatMobile
                  )}
                  , web per chat{" "}
                  {formatSavedLimit(
                    savedUser.isPremium
                      ? savedUser.globalPremiumPerChatWeb
                      : savedUser.globalPerChatWeb
                  )}
                </p>
              </div>
            ) : null}
            {userQuota ? (
              <p className="text-xs text-gray-500">
                Today: {userQuota.dailyCount}
                {userQuota.dailyLimit != null ? ` / ${userQuota.dailyLimit}` : " (no daily cap)"}
                {" · "}
                This week: {userQuota.weeklyCount}
                {userQuota.weeklyLimit != null ? ` / ${userQuota.weeklyLimit}` : " (no weekly cap)"}
                {userQuota.canSend ? "" : ` · Blocked: ${userQuota.message}`}
              </p>
            ) : null}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Daily limit</span>
                <input
                  className={`${inputCls} mt-1`}
                  type="number"
                  min="0"
                  placeholder="Inherit global"
                  value={userDaily}
                  onChange={(e) => setUserDaily(e.target.value)}
                />
                <CurrentValue
                  value={
                    savedUser?.previousDailyLimit != null
                      ? formatOverrideLimit(savedUser.previousDailyLimit)
                      : savedUser?.hasOverride
                        ? formatOverrideLimit(savedUser.dailyLimit)
                        : `${formatSavedLimit(savedUser?.dailyLimit)} (global)`
                  }
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Weekly limit</span>
                <input
                  className={`${inputCls} mt-1`}
                  type="number"
                  min="0"
                  placeholder="Inherit global"
                  value={userWeekly}
                  onChange={(e) => setUserWeekly(e.target.value)}
                />
                <CurrentValue
                  value={
                    savedUser?.previousWeeklyLimit != null
                      ? formatOverrideLimit(savedUser.previousWeeklyLimit)
                      : savedUser?.hasOverride
                        ? formatOverrideLimit(savedUser.weeklyLimit)
                        : `${formatSavedLimit(savedUser?.weeklyLimit)} (global)`
                  }
                />
              </label>
              <label className="flex flex-col justify-end gap-1 mt-2 md:mt-0">
                <span className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300"
                    checked={userRestricted}
                    onChange={(e) => setUserRestricted(e.target.checked)}
                  />
                  <span className="text-sm font-medium text-gray-800">Restrict this user</span>
                </span>
                <CurrentValue
                  value={
                    savedUser?.previousRestricted == null
                      ? savedUser?.restricted
                        ? "Yes"
                        : "No"
                      : savedUser.previousRestricted
                        ? "Yes"
                        : "No"
                  }
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-3">
              {savedUser?.isPremium ? (
                <button
                  type="button"
                  disabled={expiringPremium || savingUser}
                  onClick={expirePremiumForSelected}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-amber-800 border border-amber-200 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
                >
                  {expiringPremium ? "Expiring…" : "Expire premium"}
                </button>
              ) : null}
              <button
                type="button"
                disabled={savingUser}
                onClick={saveUser}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}
              >
                {savingUser ? "Saving…" : "Save user settings"}
              </button>
              <button
                type="button"
                onClick={() => clearUser(selectedUser.id)}
                className="px-5 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 bg-white text-gray-700"
              >
                Remove override
              </button>
            </div>
          </div>
        ) : null}
      </SectionCard>

      <SectionCard title="User overrides" subtitle="Users with custom limits or a personal chat restriction.">
        {overridesLoading ? (
          <p className="text-sm text-gray-500">Loading overrides…</p>
        ) : overrides.length === 0 ? (
          <p className="text-sm text-gray-500">No per-user overrides yet.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                    <th className="py-2 pr-3">User</th>
                    <th className="py-2 pr-3">Plan</th>
                    <th className="py-2 pr-3">Daily</th>
                    <th className="py-2 pr-3">Weekly</th>
                    <th className="py-2 pr-3">Restricted</th>
                    <th className="py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {overrides.map((row) => (
                    <tr key={row.userId} className="border-b border-gray-50">
                      <td className="py-2 pr-3">
                        <div className="font-medium text-gray-900">{displayName(row)}</div>
                        <div className="text-xs text-gray-400">ID {row.userId}</div>
                      </td>
                      <td className="py-2 pr-3">{row.isPremium ? "Premium" : "Non-premium"}</td>
                      <td className="py-2 pr-3">{formatOverrideLimit(row.dailyLimit)}</td>
                      <td className="py-2 pr-3">{formatOverrideLimit(row.weeklyLimit)}</td>
                      <td className="py-2 pr-3">{row.restricted ? "Yes" : "No"}</td>
                      <td className="py-2">
                        <button
                          type="button"
                          className="text-[#FB565F] font-semibold text-xs"
                          onClick={() =>
                            selectUser({
                              id: row.userId,
                              firstName: row.firstName,
                              lastName: row.lastName,
                              email: row.email,
                              contactNo: row.contactNo,
                            })
                          }
                        >
                          Edit
                        </button>
                        <span className="text-gray-300 mx-2">·</span>
                        <button
                          type="button"
                          className="text-gray-500 font-semibold text-xs"
                          onClick={() => clearUser(row.userId)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar
              page={overridesPage}
              totalPages={overridesTotalPages}
              totalItems={overridesTotal}
              pageSize={OVERRIDES_PAGE_SIZE}
              label="overrides"
              onPageChange={setOverridesPage}
            />
          </>
        )}
      </SectionCard>
    </div>
  );
};

export default ChatLimitsPage;
